import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import Navbar from '../components/Navbar';
import LanguageToggle from '../components/LanguageToggle';
import { useNavigate } from 'react-router-dom';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PersonalizedLearningPage = () => {
  const { t, language } = useLanguage();
  const { user } = useAuth();
  const navigate = useNavigate();
  
  const [progress, setProgress] = useState(null);
  const [weeklyBooklets, setWeeklyBooklets] = useState([]);
  const [proficiencyReport, setProficiencyReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generatingBooklet, setGeneratingBooklet] = useState(false);
  const [activeTab, setActiveTab] = useState('progress'); // progress, booklets, report
  const [showBookletForm, setShowBookletForm] = useState(false);
  const [bookletConfig, setBookletConfig] = useState({
    discipline: 'Physics',
    topics: [],
    customTopic: '',
    targetQuestionCount: 20
  });

  useEffect(() => {
    if (user) {
      fetchData();
    }
  }, [user]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [progressRes, bookletsRes, reportRes] = await Promise.all([
        axios.get(`${API}/personalization/progress`, { withCredentials: true }),
        axios.get(`${API}/personalization/weekly-booklets`, { withCredentials: true }),
        axios.get(`${API}/personalization/proficiency-report?report_type=weekly`, { withCredentials: true })
      ]);
      
      setProgress(progressRes.data);
      setWeeklyBooklets(bookletsRes.data.booklets || []);
      setProficiencyReport(reportRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      if (error.response?.status === 404) {
        // No progress yet - first time user
        setProgress({
          total_questions_attempted: 0,
          overall_accuracy: 0,
          recommended_topics: []
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateBooklet = async () => {
    // Validate form
    if (!bookletConfig.discipline) {
      alert(language === 'tr' ? 'Lütfen disiplin seçin' : 'Please select a discipline');
      return;
    }
    
    const focusAreas = bookletConfig.customTopic 
      ? [bookletConfig.customTopic]
      : bookletConfig.topics;
    
    if (focusAreas.length === 0) {
      alert(language === 'tr' ? 'Lütfen en az bir konu girin' : 'Please enter at least one topic');
      return;
    }
    
    setGeneratingBooklet(true);
    try {
      const weekNumber = weeklyBooklets.length + 1;
      
      console.log('Generating booklet with config:', bookletConfig);
      
      const response = await axios.post(
        `${API}/personalization/generate-booklet`,
        {
          week_number: weekNumber,
          target_question_count: bookletConfig.targetQuestionCount,
          include_review: true,
          difficulty_override: null,
          focus_areas: focusAreas,
          discipline: bookletConfig.discipline
        },
        { withCredentials: true }
      );
      
      console.log('Booklet response:', response.data);
      
      // Wait for DB write to complete (race condition fix)
      await new Promise(resolve => setTimeout(resolve, 800));
      
      alert(language === 'tr' 
        ? `✅ Hafta ${weekNumber} kitapçığı oluşturuldu! Görevlerim sekmesine yönlendiriliyorsunuz...` 
        : `✅ Week ${weekNumber} booklet generated! Redirecting to My Tasks...`
      );
      
      // Reset form and hide
      setShowBookletForm(false);
      setBookletConfig({
        discipline: 'Physics',
        topics: [],
        customTopic: '',
        targetQuestionCount: 20
      });
      
      // Redirect to My Tasks page where booklet will appear
      navigate('/my-tasks');
    } catch (error) {
      console.error('Booklet generation error:', error);
      console.error('Error details:', error.response?.data);
      const errorMsg = error.response?.data?.detail || error.message;
      alert(language === 'tr' 
        ? `❌ Kitapçık oluşturulamadı: ${errorMsg}` 
        : `❌ Booklet generation failed: ${errorMsg}`
      );
    } finally {
      setGeneratingBooklet(false);
    }
  };

  const getProficiencyColor = (level) => {
    const colors = {
      'beginner': 'bg-red-100 text-red-800',
      'developing': 'bg-yellow-100 text-yellow-800',
      'proficient': 'bg-blue-100 text-blue-800',
      'advanced': 'bg-green-100 text-green-800'
    };
    return colors[level] || 'bg-gray-100 text-gray-800';
  };

  const getAccuracyColor = (accuracy) => {
    if (accuracy >= 85) return 'text-green-600';
    if (accuracy >= 70) return 'text-blue-600';
    if (accuracy >= 60) return 'text-yellow-600';
    return 'text-red-600';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">{language === 'tr' ? 'Yükleniyor...' : 'Loading...'}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      <LanguageToggle />
      <Navbar title={language === 'tr' ? 'Kişiselleştirilmiş Öğrenme' : 'Personalized Learning'} />

      <div className="max-w-7xl mx-auto px-4 py-8">
        
        {/* Hero Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-2xl p-6 text-white shadow-lg">
            <div className="text-3xl font-bold mb-2">{progress?.total_questions_attempted || 0}</div>
            <div className="text-blue-100">{language === 'tr' ? 'Toplam Soru' : 'Total Questions'}</div>
          </div>
          
          <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-2xl p-6 text-white shadow-lg">
            <div className={`text-3xl font-bold mb-2`}>
              {progress?.overall_accuracy?.toFixed(1) || 0}%
            </div>
            <div className="text-green-100">{language === 'tr' ? 'Başarı Oranı' : 'Accuracy'}</div>
          </div>
          
          <div className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-2xl p-6 text-white shadow-lg">
            <div className="text-3xl font-bold mb-2">{weeklyBooklets.length}</div>
            <div className="text-purple-100">{language === 'tr' ? 'Haftalık Kitapçık' : 'Weekly Booklets'}</div>
          </div>
          
          <div className="bg-gradient-to-br from-orange-500 to-orange-600 rounded-2xl p-6 text-white shadow-lg">
            <div className="text-3xl font-bold mb-2">
              {proficiencyReport?.estimated_exam_readiness?.toFixed(0) || 0}%
            </div>
            <div className="text-orange-100">{language === 'tr' ? 'Sınav Hazırlığı' : 'Exam Readiness'}</div>
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="mb-6 flex gap-4 border-b border-gray-200">
          <button
            onClick={() => setActiveTab('progress')}
            className={`px-6 py-3 font-semibold ${
              activeTab === 'progress'
                ? 'border-b-2 border-blue-600 text-blue-600'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            📊 {language === 'tr' ? 'İlerleme' : 'Progress'}
          </button>
          <button
            onClick={() => setActiveTab('booklets')}
            className={`px-6 py-3 font-semibold ${
              activeTab === 'booklets'
                ? 'border-b-2 border-blue-600 text-blue-600'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            📚 {language === 'tr' ? 'Haftalık Kitapçıklar' : 'Weekly Booklets'}
          </button>
          <button
            onClick={() => setActiveTab('report')}
            className={`px-6 py-3 font-semibold ${
              activeTab === 'report'
                ? 'border-b-2 border-blue-600 text-blue-600'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            📈 {language === 'tr' ? 'Yeterlilik Raporu' : 'Proficiency Report'}
          </button>
        </div>

        {/* Progress Tab */}
        {activeTab === 'progress' && (
          <div className="space-y-6">
            <div className="bg-white rounded-2xl shadow-lg p-6">
              <h2 className="text-2xl font-bold text-gray-800 mb-6">
                {language === 'tr' ? 'Konu Bazlı Yeterlilik' : 'Topic-Based Proficiency'}
              </h2>
              
              {Object.keys(progress?.topic_proficiencies || {}).length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <p>{language === 'tr' ? 'Henüz veri yok. İlk kitapçığınızı çözün!' : 'No data yet. Complete your first booklet!'}</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {Object.entries(progress.topic_proficiencies).map(([topic, prof]) => (
                    <div key={topic} className="border border-gray-200 rounded-lg p-4">
                      <div className="flex justify-between items-center mb-2">
                        <h3 className="font-semibold text-gray-800">{topic}</h3>
                        <span className={`px-3 py-1 rounded-full text-sm font-semibold ${getProficiencyColor(prof.proficiency_level)}`}>
                          {prof.proficiency_level}
                        </span>
                      </div>
                      <div className="flex justify-between text-sm text-gray-600 mb-2">
                        <span>{prof.questions_attempted} {language === 'tr' ? 'soru' : 'questions'}</span>
                        <span className={`font-semibold ${getAccuracyColor(prof.accuracy)}`}>
                          {prof.accuracy.toFixed(1)}% {language === 'tr' ? 'doğruluk' : 'accuracy'}
                        </span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div
                          className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                          style={{ width: `${prof.accuracy}%` }}
                        ></div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {progress?.recommended_topics?.length > 0 && (
              <div className="bg-yellow-50 border border-yellow-200 rounded-2xl p-6">
                <h3 className="text-lg font-bold text-yellow-800 mb-3">
                  💡 {language === 'tr' ? 'Önerilen Çalışma Konuları' : 'Recommended Study Topics'}
                </h3>
                <div className="flex flex-wrap gap-2">
                  {progress.recommended_topics.map((topic) => (
                    <span key={topic} className="bg-yellow-100 text-yellow-800 px-3 py-1 rounded-full text-sm">
                      {topic}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Booklets Tab */}
        {activeTab === 'booklets' && (
          <div className="space-y-6">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold text-gray-800">
                {language === 'tr' ? 'Haftalık Kitapçıklar' : 'Weekly Booklets'}
              </h2>
              <button
                onClick={() => setShowBookletForm(!showBookletForm)}
                className="bg-gradient-to-r from-blue-600 to-purple-600 text-white px-6 py-3 rounded-xl hover:shadow-lg transition-all duration-300 font-semibold"
              >
                {language === 'tr' ? '✨ Yeni Kitapçık Oluştur' : '✨ Generate New Booklet'}
              </button>
            </div>
            
            {/* Booklet Configuration Form */}
            {showBookletForm && (
              <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-2xl shadow-lg p-6 mb-6">
                <h3 className="text-xl font-bold text-gray-800 mb-4">
                  {language === 'tr' ? '📝 Kitapçık Yapılandırması' : '📝 Booklet Configuration'}
                </h3>
                
                {/* Discipline Selection */}
                <div className="mb-4">
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    {language === 'tr' ? 'Disiplin *' : 'Discipline *'}
                  </label>
                  <select
                    value={bookletConfig.discipline}
                    onChange={(e) => setBookletConfig({...bookletConfig, discipline: e.target.value})}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="Physics">{language === 'tr' ? 'Fizik' : 'Physics'}</option>
                    <option value="Chemistry">{language === 'tr' ? 'Kimya' : 'Chemistry'}</option>
                    <option value="Biology">{language === 'tr' ? 'Biyoloji' : 'Biology'}</option>
                    <option value="Mathematics">{language === 'tr' ? 'Matematik' : 'Mathematics'}</option>
                  </select>
                </div>
                
                {/* Topics */}
                <div className="mb-4">
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    {language === 'tr' ? 'Konu/Konular *' : 'Topic(s) *'}
                  </label>
                  <input
                    type="text"
                    value={bookletConfig.customTopic}
                    onChange={(e) => setBookletConfig({...bookletConfig, customTopic: e.target.value})}
                    placeholder={language === 'tr' ? 'Örn: Newton Yasaları, Kuvvet ve Hareket' : 'e.g., Newton\'s Laws, Force and Motion'}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    {language === 'tr' ? 'Virgülle ayırarak birden fazla konu girebilirsiniz' : 'You can enter multiple topics separated by commas'}
                  </p>
                </div>
                
                {/* Question Count */}
                <div className="mb-4">
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    {language === 'tr' ? 'Soru Sayısı' : 'Number of Questions'}
                  </label>
                  <input
                    type="number"
                    value={bookletConfig.targetQuestionCount}
                    onChange={(e) => setBookletConfig({...bookletConfig, targetQuestionCount: parseInt(e.target.value)})}
                    min="5"
                    max="50"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                
                {/* Action Buttons */}
                <div className="flex gap-3">
                  <button
                    onClick={handleGenerateBooklet}
                    disabled={generatingBooklet}
                    className="flex-1 bg-gradient-to-r from-green-600 to-blue-600 text-white px-6 py-3 rounded-xl hover:shadow-lg disabled:opacity-50 transition-all font-semibold"
                  >
                    {generatingBooklet 
                      ? (language === 'tr' ? '⏳ Oluşturuluyor...' : '⏳ Generating...') 
                      : (language === 'tr' ? '✅ Kitapçığı Oluştur' : '✅ Create Booklet')
                    }
                  </button>
                  <button
                    onClick={() => setShowBookletForm(false)}
                    className="px-6 py-3 border-2 border-gray-300 text-gray-700 rounded-xl hover:bg-gray-100 transition-all font-semibold"
                  >
                    {language === 'tr' ? 'İptal' : 'Cancel'}
                  </button>
                </div>
              </div>
            )}

            {weeklyBooklets.length === 0 ? (
              <div className="bg-white rounded-2xl shadow-lg p-12 text-center">
                <p className="text-gray-500 mb-4">
                  {language === 'tr' ? 'Henüz kitapçık yok.' : 'No booklets yet.'}
                </p>
                <button
                  onClick={handleGenerateBooklet}
                  className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700"
                >
                  {language === 'tr' ? 'İlk Kitapçığı Oluştur' : 'Create First Booklet'}
                </button>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {weeklyBooklets.map((booklet) => (
                  <div key={booklet.id} className="bg-white rounded-2xl shadow-lg p-6 hover:shadow-xl transition">
                    <div className="flex justify-between items-start mb-4">
                      <h3 className="text-lg font-bold text-gray-800">
                        {language === 'tr' ? 'Hafta' : 'Week'} {booklet.week_number}
                      </h3>
                      <span className={`px-3 py-1 rounded-full text-sm font-semibold ${
                        booklet.status === 'completed' ? 'bg-green-100 text-green-800' :
                        booklet.status === 'in_progress' ? 'bg-blue-100 text-blue-800' :
                        'bg-gray-100 text-gray-800'
                      }`}>
                        {booklet.status}
                      </span>
                    </div>
                    
                    <div className="space-y-2 text-sm text-gray-600 mb-4">
                      <p>📝 {booklet.question_ids?.length || 0} {language === 'tr' ? 'soru' : 'questions'}</p>
                      <p>⏱️ ~{booklet.expected_duration_minutes} {language === 'tr' ? 'dakika' : 'minutes'}</p>
                      {booklet.score && (
                        <p className="font-semibold text-blue-600">🎯 Puan: {booklet.score}%</p>
                      )}
                    </div>

                    <button
                      onClick={() => navigate(`/booklet/${booklet.id}/take`)}
                      disabled={booklet.status === 'completed'}
                      className="w-full bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition"
                    >
                      {booklet.status === 'completed' 
                        ? (language === 'tr' ? 'Tamamlandı' : 'Completed')
                        : (language === 'tr' ? 'Başla' : 'Start')
                      }
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Report Tab */}
        {activeTab === 'report' && proficiencyReport && (
          <div className="space-y-6">
            <div className="bg-white rounded-2xl shadow-lg p-6">
              <h2 className="text-2xl font-bold text-gray-800 mb-6">
                {language === 'tr' ? 'Haftalık Yeterlilik Raporu' : 'Weekly Proficiency Report'}
              </h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Strong Topics */}
                <div>
                  <h3 className="text-lg font-semibold text-green-700 mb-4">
                    ✅ {language === 'tr' ? 'Güçlü Konular' : 'Strong Topics'}
                  </h3>
                  {proficiencyReport.strong_topics?.length === 0 ? (
                    <p className="text-gray-500 text-sm">{language === 'tr' ? 'Henüz veri yok' : 'No data yet'}</p>
                  ) : (
                    <div className="space-y-3">
                      {proficiencyReport.strong_topics?.map((topic, i) => (
                        <div key={i} className="border border-green-200 rounded-lg p-3 bg-green-50">
                          <div className="flex justify-between items-center">
                            <span className="font-medium text-gray-800">{topic.topic}</span>
                            <span className="text-green-700 font-semibold">{topic.proficiency.toFixed(1)}%</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Weak Topics */}
                <div>
                  <h3 className="text-lg font-semibold text-red-700 mb-4">
                    ⚠️ {language === 'tr' ? 'Geliştirilmesi Gereken Konular' : 'Topics to Improve'}
                  </h3>
                  {proficiencyReport.weak_topics?.length === 0 ? (
                    <p className="text-gray-500 text-sm">{language === 'tr' ? 'Harika! Zayıf konu yok.' : 'Great! No weak topics.'}</p>
                  ) : (
                    <div className="space-y-3">
                      {proficiencyReport.weak_topics?.map((topic, i) => (
                        <div key={i} className="border border-red-200 rounded-lg p-3 bg-red-50">
                          <div className="flex justify-between items-center">
                            <span className="font-medium text-gray-800">{topic.topic}</span>
                            <span className="text-red-700 font-semibold">{topic.proficiency.toFixed(1)}%</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Next Focus Areas */}
              {proficiencyReport.next_focus_areas?.length > 0 && (
                <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <h3 className="font-semibold text-blue-800 mb-3">
                    🎯 {language === 'tr' ? 'Sonraki Odak Alanları' : 'Next Focus Areas'}
                  </h3>
                  <div className="flex flex-wrap gap-2">
                    {proficiencyReport.next_focus_areas.map((area) => (
                      <span key={area} className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm">
                        {area}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PersonalizedLearningPage;
